import { faArrowLeft, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon } from '@mantine/core';
import { DEFAULT_CARD_COLOR } from '@modules/card-helpers';
import { createChildLogger } from '@modules/logger';
import { LayoutSwitcherCarousel } from 'app/[workspace]/identity/_components/DesignPanel/LayoutSwitcherCarousel';
import type { CardLayoutKind, ImageField } from 'app/[workspace]/identity/_lib/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type CardImage, CardLayout, type CardPicturesFields } from 'types';

import * as styles from './LayoutPicker.css';

const logger = createChildLogger({ module: 'components/CustomizeCard/LayoutPicker' });

/**
 * The identity editor's layout kinds that the onboarding card can persist. The
 * two vocabularies coincide by value today, but the carousel owns its list, so
 * an unmapped kind is dropped here rather than written to the card as an
 * unknown layout string.
 */
const LAYOUT_BY_KIND: Partial<Record<CardLayoutKind, CardLayout>> = {
  '1A': CardLayout.Layout_1A,
  '1B': CardLayout.Layout_1B,
  '1C': CardLayout.Layout_1C,
  '2A': CardLayout.Layout_2A,
  '2B': CardLayout.Layout_2B,
  '2C': CardLayout.Layout_2C,
  '2D': CardLayout.Layout_2D,
  '3A': CardLayout.Layout_3A,
};

interface LayoutPickerProps extends CardPicturesFields {
  cardColor: string | undefined;
  currentLayout: CardLayout | undefined;
  onSelect: (layout: CardLayout) => void;
}

interface ScrollState {
  x: number;
  max: number;
  page: number;
}

/**
 * Adapts an onboarding card image to the shape the identity editor's layout
 * switcher reads. Only the URLs matter to it; the rest is the field envelope the
 * unified-profile form carries, which the onboarding card doesn't have.
 */
function toImageField(image: CardImage | null | undefined): ImageField | null {
  if (!image?.editedImageUrl && !image?.originalImageUrl) {
    return null;
  }

  return {
    id: '',
    isEditable: true,
    isTemplated: false,
    valueProvider: 'profile',
    editedUrl: image.editedImageUrl ?? null,
    originalUrl: image.originalImageUrl ?? null,
    metadata: null,
  };
}

/**
 * Inline layout chooser for the customize-card step. Reuses the identity
 * editor's layout switcher so the two surfaces look and behave the same, and
 * adds the paging controls from the onboarding design: edge fades that show
 * there is more to scroll, arrows, and a dot per scroll page.
 */
export const LayoutPicker: React.FC<LayoutPickerProps> = ({
  cardColor,
  companyLogoImg,
  coverPhotoImg,
  profilePictureImg,
  currentLayout,
  onSelect,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<ScrollState>({ x: 0, max: 0, page: 0 });

  const images = useMemo(
    () => ({
      profilePicture: toImageField(profilePictureImg),
      coverPhoto: toImageField(coverPhotoImg),
      companyLogo: toImageField(companyLogoImg),
    }),
    [profilePictureImg, coverPhotoImg, companyLogoImg],
  );

  // Scroll events arrive every frame; only re-render when something derived changes.
  const syncScroll = useCallback(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const next: ScrollState = {
      x: viewport.scrollLeft,
      max: viewport.scrollWidth - viewport.clientWidth,
      page: viewport.clientWidth,
    };

    setScroll((previous) =>
      previous.x === next.x && previous.max === next.max && previous.page === next.page
        ? previous
        : next,
    );
  }, []);

  // The strip's width can settle after mount (late layout, font swap, an
  // enclosing transition), so watch the element itself rather than measuring once.
  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    syncScroll();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncScroll);
      return () => window.removeEventListener('resize', syncScroll);
    }

    const observer = new ResizeObserver(syncScroll);
    observer.observe(viewport);
    for (const child of viewport.children) {
      observer.observe(child);
    }
    return () => observer.disconnect();
  }, [syncScroll]);

  const canScrollBack = scroll.x > 1;
  const canScrollForward = scroll.x < scroll.max - 1;
  // The dots and arrows page through the strip one viewport at a time. How many
  // layouts fit per page depends on the width, so the count is derived from the
  // measured scroll range rather than the number of layouts. Sitting at the end
  // counts as the last page even when the final page is only partly full.
  const pageCount = scroll.page > 0 ? Math.ceil((scroll.max + scroll.page) / scroll.page) : 1;
  const currentPage = !canScrollForward
    ? pageCount - 1
    : Math.min(pageCount - 1, scroll.page > 0 ? Math.round(scroll.x / scroll.page) : 0);

  const scrollToPage = (index: number) => {
    viewportRef.current?.scrollTo({
      left: Math.min(index * scroll.page, scroll.max),
      behavior: 'smooth',
    });
  };

  const handleSelect = (kind: CardLayoutKind) => {
    const layout = LAYOUT_BY_KIND[kind];

    if (!layout) {
      logger.warn('Ignored a layout the onboarding card cannot persist', { layout_kind: kind });
      return;
    }

    onSelect(layout);
  };

  return (
    <div className={styles.root}>
      <div className={styles.viewport}>
        <div className={styles.fade.left} data-visible={canScrollBack} aria-hidden />
        <LayoutSwitcherCarousel
          cardColor={cardColor ?? DEFAULT_CARD_COLOR}
          currentLayout={currentLayout ?? 'blinq-logo'}
          images={images}
          onSelect={handleSelect}
          viewportRef={viewportRef}
          onScrollPositionChange={syncScroll}
        />
        <div className={styles.fade.right} data-visible={canScrollForward} aria-hidden />
      </div>
      <div className={styles.controls}>
        <ActionIcon
          variant="default"
          radius="md"
          size="xl"
          onClick={() => scrollToPage(currentPage - 1)}
          disabled={!canScrollBack}
          aria-label="Previous layouts"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </ActionIcon>
        <ul className={styles.dots}>
          {Array.from({ length: pageCount }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the dots are positional by nature
            <li key={index}>
              <button
                type="button"
                className={styles.dot}
                aria-label={`Go to page ${index + 1} of layouts`}
                aria-current={index === currentPage}
                onClick={() => scrollToPage(index)}
              />
            </li>
          ))}
        </ul>
        <ActionIcon
          variant="default"
          radius="md"
          size="xl"
          onClick={() => scrollToPage(currentPage + 1)}
          disabled={!canScrollForward}
          aria-label="Next layouts"
        >
          <FontAwesomeIcon icon={faArrowRight} />
        </ActionIcon>
      </div>
    </div>
  );
};

export default LayoutPicker;
